<template>
  <v-dialog :model-value="modelValue" max-width="900px" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">Статистика пользователя {{ user?.username }}</span>
        <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)"></v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="isLoading" indeterminate class="mb-4"></v-progress-linear>
        <v-alert v-else-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

        <template v-else-if="stats">
          <div class="text-caption text-disabled mb-3">
            <template v-if="stats.calculated_at">Сообщения и место посчитаны {{ formatDateTime(stats.calculated_at) }}.</template>
            <template v-else>Сообщения и место ещё не посчитаны.</template>
            Расход токенов — на текущий момент.
          </div>

          <!-- Себестоимость -->
          <div class="text-subtitle-1 font-weight-bold mb-1">Себестоимость</div>
          <v-table density="compact" class="mb-2">
            <thead>
              <tr>
                <th>Раздел</th>
                <th>Модель</th>
                <th class="text-right">Запросов</th>
                <th class="text-right">Токены, вход</th>
                <th class="text-right">Токены, выход</th>
                <th class="text-right">$</th>
                <th class="text-right">₽</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in stats.costs" :key="row.feature + row.provider + row.model">
                <td>{{ featureLabel(row.feature) }}</td>
                <td class="text-caption">{{ row.provider }} / {{ row.model }}</td>
                <td class="text-right">{{ formatNumber(row.request_count) }}</td>
                <td class="text-right">{{ formatNumber(row.prompt_tokens) }}</td>
                <td class="text-right">{{ formatNumber(row.completion_tokens) }}</td>
                <td class="text-right">{{ formatMoney(row.cost_usd, 4) }}</td>
                <td class="text-right">
                  {{ formatMoney(row.cost_rub) }}
                  <v-tooltip v-if="row.unpriced_requests" location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" color="warning">mdi-alert-circle-outline</v-icon>
                    </template>
                    Не посчитано запросов: {{ row.unpriced_requests }} — нет цены модели или курса
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="!stats.costs.length">
                <td colspan="7" class="text-center text-disabled">Расхода токенов нет</td>
              </tr>
              <tr class="font-weight-bold">
                <td colspan="5">Собственный расход</td>
                <td class="text-right">{{ formatMoney(stats.own_cost_usd, 4) }}</td>
                <td class="text-right">{{ formatMoney(stats.own_cost_rub) }}</td>
              </tr>
              <tr>
                <td colspan="5">Доля общих расходов ({{ formatPercent(stats.shared_share) }} сообщений)</td>
                <td class="text-right">{{ formatMoney(stats.shared_cost_usd, 4) }}</td>
                <td class="text-right">{{ formatMoney(stats.shared_cost_rub) }}</td>
              </tr>
              <tr class="font-weight-bold">
                <td colspan="5">Итого</td>
                <td class="text-right">{{ formatMoney(stats.total_cost_usd, 4) }}</td>
                <td class="text-right">{{ formatMoney(stats.total_cost_rub) }}</td>
              </tr>
            </tbody>
          </v-table>
          <div class="text-caption mb-4">
            «Итого» — накопленная себестоимость за всё время учёта.
          </div>

          <!-- По месяцам -->
          <div class="text-subtitle-1 font-weight-bold mb-1">Себестоимость по месяцам</div>
          <v-table density="compact" class="mb-4">
            <thead>
              <tr>
                <th>Месяц</th>
                <th class="text-right">Собственный расход, ₽</th>
                <th class="text-right">Доля общих, ₽</th>
                <th class="text-right">Итого, $</th>
                <th class="text-right">Итого, ₽</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in stats.monthly_costs" :key="row.month">
                <td>{{ formatMonth(row.month) }}</td>
                <td class="text-right">{{ formatMoney(row.own_cost_rub) }}</td>
                <td class="text-right">{{ formatMoney(row.shared_cost_rub) }}</td>
                <td class="text-right">{{ formatMoney(row.total_cost_usd, 4) }}</td>
                <td class="text-right font-weight-bold">{{ formatMoney(row.total_cost_rub) }}</td>
              </tr>
              <tr v-if="!stats.monthly_costs.length">
                <td colspan="5" class="text-center text-disabled">Расхода нет</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Сообщения -->
          <div class="text-subtitle-1 font-weight-bold mb-1">Сообщения</div>
          <div class="text-body-2 mb-1">
            По проектам: {{ formatNumber(stats.findings_count) }} ·
            по собственным источникам: {{ formatNumber(stats.own_messages_count) }} ·
            <b>всего: {{ formatNumber(stats.findings_count + stats.own_messages_count) }}</b>
          </div>
          <v-table density="compact" class="usage-counts mb-4">
            <tbody>
              <tr v-for="item in stats.findings_by_project" :key="item.id">
                <td>{{ item.name || ('Проект ' + item.id) }}</td>
                <td class="text-right usage-count">{{ formatNumber(item.count) }}</td>
              </tr>
              <tr v-if="!stats.findings_by_project.length">
                <td class="text-disabled">Проектов нет</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Место -->
          <div class="text-subtitle-1 font-weight-bold mb-1">
            Место: {{ formatBytes(stats.es_bytes + stats.db_bytes + stats.files_bytes) }}
          </div>
          <v-table density="compact">
            <tbody>
              <tr v-for="row in storageRows" :key="row.key">
                <td>{{ row.label }}</td>
                <td class="text-right">{{ formatBytes(row.bytes) }}</td>
              </tr>
            </tbody>
          </v-table>
          <div class="text-caption text-disabled mt-1">
            Elasticsearch — оценка: число документов на средний размер документа в индексе.
            PostgreSQL — размер строк без индексов.
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import apiClient from '@/apiClient'
import { featureLabel, formatBytes, formatMoney, formatNumber } from '@/utils/usageStats'

const STORAGE_LABELS = {
  es_findings: 'Elasticsearch: сообщения проектов',
  es_own_content: 'Elasticsearch: документы собственных источников',
  files_reports: 'Файлы отчётов',
  db_projects: 'PostgreSQL: проекты',
  db_project_queries: 'PostgreSQL: запросы проектов',
  db_project_entities: 'PostgreSQL: сущности',
  db_project_entity_mentions: 'PostgreSQL: упоминания сущностей',
  db_reports: 'PostgreSQL: отчёты',
  db_report_templates: 'PostgreSQL: шаблоны отчётов',
  db_user_sources: 'PostgreSQL: собственные источники'
}

export default {
  name: 'UserUsageDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    user: { type: Object, default: null }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      stats: null,
      isLoading: false,
      error: ''
    }
  },
  computed: {
    storageRows() {
      const storage = this.stats?.storage || {}
      return Object.keys(storage)
        .map(key => ({ key, label: STORAGE_LABELS[key] || key, bytes: storage[key] }))
        .sort((a, b) => b.bytes - a.bytes)
    }
  },
  watch: {
    modelValue(isOpen) {
      if (isOpen && this.user) this.loadStats()
    }
  },
  methods: {
    featureLabel,
    formatBytes,
    formatMoney,
    formatNumber,
    async loadStats() {
      this.isLoading = true
      this.error = ''
      this.stats = null
      try {
        const response = await apiClient.get(`/admin/usage/users/${this.user.id}`)
        this.stats = response.data
      } catch (error) {
        this.error = 'Ошибка загрузки статистики: ' + (error.response?.data?.detail || error.message)
      } finally {
        this.isLoading = false
      }
    },
    formatMonth(month) {
      const [year, monthNumber] = month.split('-').map(Number)
      const text = new Date(year, monthNumber - 1, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
    formatPercent(share) {
      return ((share || 0) * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + '%'
    },
    formatDateTime(value) {
      return new Date(value).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* Длинные названия переносятся, а не выталкивают счётчик за край окна */
.usage-counts :deep(table) {
  table-layout: fixed;
}
.usage-counts td {
  overflow-wrap: anywhere;
}
.usage-count {
  width: 90px;
  white-space: nowrap;
}
</style>
